import { deepMerge } from '@dawn/core/objects';
import type { PartialAdminSettingsInput } from '@dawn/feature-config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/queries/query-keys';
import { SETTINGS_DEFAULTS, type EffectiveSettings } from '@/hooks/queries/use-settings-query';
import { settingsApi } from '@/lib/api';

/**
 * useUpdateAdminSettings — PATCH /settings/admin with the full optimistic
 * pattern from TDEV-266 design Decision #21 (plan.md:1154-1200).
 *
 * Pattern:
 * - `onMutate`: cancel in-flight refetches, snapshot the previous cache, then
 *   write the optimistic blob via `deepMerge(curr, partial)`.
 * - `onError`: restore the snapshot from the mutation context + log for dev
 *   diagnostics.
 * - `onSuccess`: OVERLAY the row-shaped server response onto the existing
 *   effective cache via `deepMerge`. Per design.md §3.2 the endpoint returns
 *   the raw admin row (AdminSettingsOutput), NOT the merged EffectiveSettings
 *   blob — pinning the response directly would clobber tenant-derived keys
 *   (e.g. `dashboard`) that are absent from the row. Do NOT invalidate inside
 *   onSuccess — invalidating would trigger a refetch race where a concurrent
 *   optimistic PATCH could resurrect stale data.
 * - `onSettled`: skip-invalidate-while-pending. Only fire `invalidateQueries`
 *   when no other mutation of the same key is in flight. This collapses N
 *   concurrent mutate() calls into at most one tail-end refetch, eliminating
 *   the optimistic/refetch race entirely (PP13).
 */
export function useUpdateAdminSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['admin-settings:patch'],
    mutationFn: (partial: PartialAdminSettingsInput) => settingsApi.patchAdminSettings(partial),

    onMutate: async (partial) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.settings.root });
      const previous = queryClient.getQueryData<EffectiveSettings>(queryKeys.settings.root);
      queryClient.setQueryData<EffectiveSettings>(queryKeys.settings.root, (curr) =>
        deepMerge(curr ?? SETTINGS_DEFAULTS, partial)
      );
      return { previous };
    },

    onError: (err, _partial, context) => {
      // PII standard R4: server errors may echo back user input. Gate the raw
      // error log to dev so production logs cannot leak PII via the error
      // object. `import.meta.env.DEV` is the Vite-idiomatic equivalent of
      // `NODE_ENV === 'development'` and tree-shakes to `false` in prod.
      if (import.meta.env.DEV) {
        console.error('[LOCAL ONLY] TDEV-266 admin settings mutation failed', err);
      }
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.settings.root, context.previous);
      }
    },

    onSuccess: (serverResponse) => {
      // REM-FIX (design.md §3.2): serverResponse is AdminSettingsOutput (the
      // raw admin row — admin-only keys + optional theme/dashboard overrides).
      // Overlay it onto the effective cache via deepMerge so tenant-derived
      // keys (`dashboard`) and admin-only keys absent from this response
      // survive. Decision #21 explicitly forbids invalidating inside
      // onSuccess — that path is what onSettled handles (and only when no
      // peer mutations are pending).
      queryClient.setQueryData<EffectiveSettings>(queryKeys.settings.root, (curr) =>
        deepMerge(curr ?? SETTINGS_DEFAULTS, serverResponse)
      );
    },

    onSettled: () => {
      // Skip-invalidate-while-pending (PP13 — plan.md:1187, Decision #21).
      //
      // `isMutating` returns the count of mutations whose status is still
      // `'pending'`. TanStack Query v5 calls `onSettled` BEFORE dispatching
      // the final `'success' | 'error'` transition, so the mutation currently
      // running its `onSettled` is itself counted as pending. The canonical
      // predicate that means "I am the last pending peer" is therefore
      // `isMutating(...) === 1`.
      //
      // Plan deviation note: plan.md:1187 wrote `=== 0` from the older v4-era
      // pattern where the dispatch happened before `onSettled`. Using `=== 0`
      // here would suppress invalidate forever, defeating the stated runtime
      // intent ("invalidate exactly once after the last peer settles"). We
      // honor the plan's behavior, not the off-by-one literal.
      if (queryClient.isMutating({ mutationKey: ['admin-settings:patch'] }) === 1) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.settings.root });
      }
    },
  });
}
